import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

let loadPromise = null;

// Load the Google Maps JS SDK once (key served by the backend).
export const loadGoogleMaps = async (tokenKey = "token") => {
  if (window.google?.maps?.Map) return window.google.maps;
  if (!loadPromise) {
    loadPromise = (async () => {
      const token =
        localStorage.getItem(tokenKey) ||
        localStorage.getItem("tech_token") ||
        localStorage.getItem("token");
      const res = await axios.get(`${API}/api/config/maps`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const key = res.data.maps_key;
      await new Promise((resolve, reject) => {
        window.__gmapsReady = () => resolve();
        const s = document.createElement("script");
        s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=geometry&loading=async&callback=__gmapsReady`;
        s.async = true;
        s.onerror = () => reject(new Error("Google Maps failed to load"));
        document.head.appendChild(s);
      });
      return window.google.maps;
    })().catch((e) => {
      loadPromise = null;
      throw e;
    });
  }
  return loadPromise;
};

// Open a URL outside the app shell — native bridge when inside the mobile app.
export const openExternal = (url) => {
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({ type: "OPEN_EXTERNAL", payload: { url } })
    );
  } else {
    window.open(url, "_blank", "noopener");
  }
};

export const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent || "");

export const googleNavUrl = (dest) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;

export const appleNavUrl = (dest) => `https://maps.apple.com/?daddr=${encodeURIComponent(dest)}`;

export const formatDistance = (meters) => {
  if (meters == null) return "—";
  const mi = meters / 1609.34;
  return mi >= 10 ? `${Math.round(mi)} mi` : `${mi.toFixed(1)} mi`;
};

export const formatDuration = (seconds) => {
  if (seconds == null) return "—";
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

export const timeAgo = (iso) => {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

// Large, high-contrast teardrop pin for job markers. `withDot` renders a
// white center dot (single jobs); omit it when a number/label sits in the head.
export const pinIcon = (maps, color, { size = 40, withDot = true } = {}) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24">
    <path d="M12 0.8C7.3 0.8 3.6 4.5 3.6 9.2c0 5.9 7.2 13.3 8 14.1a0.55 0.55 0 0 0 0.8 0c0.8-0.8 8-8.2 8-14.1C20.4 4.5 16.7 0.8 12 0.8z" fill="${color}" stroke="#ffffff" stroke-width="1.5"/>
    ${withDot ? '<circle cx="12" cy="9.2" r="3.2" fill="#ffffff"/>' : ""}
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new maps.Size(size, size),
    anchor: new maps.Point(size / 2, size * 0.98),
    labelOrigin: new maps.Point(size / 2, size * 0.39),
  };
};
